import { useState, useEffect } from "react";
import { useParams, Link } from "react-router-dom";
import { motion } from "framer-motion";
import axios from "axios";
import { Music, Play, Headphones, Heart, Share2, ExternalLink, Loader2 } from "lucide-react";
import { Badge } from "../components/ui/badge";
import { Button } from "../components/ui/button";
import BeatPlayer from "../components/BeatPlayer";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const ProducerStorefrontPage = () => {
  const { producerId } = useParams();
  const [producer, setProducer] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    const fetchProfile = async () => {
      try {
        const res = await axios.get(`${API}/producers/profile/${producerId}`);
        setProducer(res.data);
      } catch (e) {
        setError(e.response?.status === 404 ? "Producer not found" : "Failed to load profile");
      } finally { setLoading(false); }
    };
    fetchProfile();
  }, [producerId]);

  if (loading) return (
    <div className="min-h-screen flex items-center justify-center">
      <Loader2 className="w-8 h-8 animate-spin text-neon-cyan" />
    </div>
  );

  if (error) return (
    <div className="min-h-screen flex items-center justify-center px-6">
      <div className="glass p-12 text-center max-w-lg">
        <Music className="w-16 h-16 text-zinc-600 mx-auto mb-6" />
        <h1 className="font-heading text-3xl uppercase mb-4">{error}</h1>
        <Link to="/producers"><Button className="bg-neon-cyan text-black">Back to Producers</Button></Link>
      </div>
    </div>
  );

  const stats = producer?.stats || {};
  const socials = producer?.social_links || {};

  return (
    <div className="min-h-screen" data-testid="producer-storefront">
      {/* Banner */}
      <div className="relative h-64 md:h-80 overflow-hidden">
        {producer.banner_url ? (
          <img src={producer.banner_url} alt="Banner" className="w-full h-full object-cover" />
        ) : (
          <div className="w-full h-full bg-gradient-to-br from-neon-cyan/20 via-zinc-900 to-neon-magenta/20" />
        )}
        <div className="absolute inset-0 bg-gradient-to-t from-black via-black/50 to-transparent" />
        
        {/* Profile area on banner */}
        <div className="absolute bottom-0 left-0 right-0 px-6 pb-8">
          <div className="max-w-5xl mx-auto flex items-end gap-6">
            <div className="w-24 h-24 md:w-32 md:h-32 rounded-full border-4 border-neon-cyan overflow-hidden bg-zinc-800 flex-shrink-0">
              {producer.avatar_url ? (
                <img src={producer.avatar_url} alt={producer.artist_name} className="w-full h-full object-cover" />
              ) : (
                <div className="w-full h-full flex items-center justify-center bg-gradient-to-br from-neon-cyan/30 to-neon-magenta/30">
                  <span className="font-heading text-3xl md:text-4xl">{producer.artist_name?.[0]?.toUpperCase()}</span>
                </div>
              )}
            </div>
            <div className="flex-1 min-w-0 pb-1">
              <Badge className="bg-neon-green/20 text-neon-green border-neon-green/50 mb-2 text-xs">Verified Producer</Badge>
              <h1 className="font-heading text-3xl md:text-4xl lg:text-5xl uppercase tracking-tight truncate" data-testid="producer-name">
                {producer.artist_name}
              </h1>
              {producer.tagline && (
                <p className="text-zinc-400 text-sm mt-1">{producer.tagline}</p>
              )}
            </div>
          </div>
        </div>
      </div>

      <div className="max-w-5xl mx-auto px-6 py-10">
        {/* Stats & Social */}
        <div className="flex flex-col md:flex-row gap-6 mb-10">
          {/* Stats */}
          <div className="grid grid-cols-4 gap-4 flex-1">
            {[
              { label: "Beats", value: stats.beat_count || 0, color: "text-neon-cyan" },
              { label: "Plays", value: (stats.total_plays || 0).toLocaleString(), color: "text-neon-green" },
              { label: "Likes", value: stats.total_likes || 0, color: "text-red-400" },
              { label: "Sales", value: stats.total_sales || 0, color: "text-neon-yellow" },
            ].map(({ label, value, color }) => (
              <div key={label} className="glass p-4 text-center" data-testid={`stat-${label.toLowerCase()}`}>
                <p className={`font-heading text-xl md:text-2xl ${color}`}>{value}</p>
                <p className="text-[10px] text-zinc-500 uppercase tracking-wide">{label}</p>
              </div>
            ))}
          </div>

          {/* Social Links */}
          <div className="flex flex-wrap gap-2 items-start">
            {Object.entries(socials).filter(([_, v]) => v).map(([platform, url]) => (
              <a key={platform} href={url} target="_blank" rel="noreferrer">
                <Button variant="outline" size="sm" className="border-white/10 hover:border-neon-cyan/50 text-zinc-400 hover:text-neon-cyan" data-testid={`social-${platform}`}>
                  <ExternalLink className="w-3 h-3 mr-1" />
                  {platform.charAt(0).toUpperCase() + platform.slice(1)}
                </Button>
              </a>
            ))}
            {producer.portfolio_url && (
              <a href={producer.portfolio_url} target="_blank" rel="noreferrer">
                <Button variant="outline" size="sm" className="border-white/10 hover:border-neon-magenta/50 text-zinc-400 hover:text-neon-magenta">
                  <ExternalLink className="w-3 h-3 mr-1" />Portfolio
                </Button>
              </a>
            )}
          </div>
        </div>

        {/* Bio */}
        {producer.bio && (
          <div className="glass p-6 mb-10">
            <h2 className="font-heading text-sm uppercase text-zinc-500 mb-3">About</h2>
            <p className="text-zinc-300 text-sm leading-relaxed whitespace-pre-wrap">{producer.bio}</p>
          </div>
        )}

        {/* Beats Catalog */}
        <div>
          <div className="flex items-center gap-3 mb-6">
            <Headphones className="w-5 h-5 text-neon-cyan" />
            <h2 className="font-heading text-xl uppercase">Beats by {producer.artist_name}</h2>
            <Badge className="bg-white/5 text-zinc-400 border-white/10 text-xs">{stats.beat_count || 0}</Badge>
          </div>
          
          {producer.beats?.length === 0 ? (
            <div className="glass p-12 text-center">
              <Music className="w-12 h-12 text-zinc-600 mx-auto mb-3" />
              <p className="text-zinc-500">No beats available yet. Check back soon!</p>
            </div>
          ) : (
            <div className="space-y-3">
              {producer.beats?.map((beat, index) => (
                <motion.div
                  key={beat.beat_id}
                  initial={{ opacity: 0, y: 15 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ delay: index * 0.05 }}
                >
                  <BeatPlayer beat={beat} />
                </motion.div>
              ))}
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default ProducerStorefrontPage;
